import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, GitCompareArrows, Palette, Clock, FileText, RotateCcw } from 'lucide-react';
import type { ArsColorCode, BarDiagramData, DiagramMetadata } from '../../types/barDiagram';
import { arsColorStyles, arsColorLabels } from './barDiagramColors';
import { useState } from 'react';

interface Props {
  data: BarDiagramData;
  originalData: BarDiagramData;
  collapsed: boolean;
  onToggle: () => void;
  onReset: () => void;
}

/* ── Acordeón ── */
function Section({
  icon: Icon,
  title,
  count,
  defaultOpen = true,
  children,
}: {
  icon: React.ElementType;
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;

  return (
    <div className="border-b border-gray-800">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-800/50 transition-colors"
      >
        <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="flex-1 text-left">{title}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-900/50 text-orange-400 font-medium">
          {count}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
        />
      </button>
      {open && <div className="px-4 pb-4 text-sm text-gray-400 space-y-2">{children}</div>}
    </div>
  );
}

/* ── Swatch mini ── */
function Swatch({ code }: { code: ArsColorCode }) {
  return (
    <span
      className="inline-block rounded-sm border border-gray-600 flex-shrink-0"
      style={{ width: 16, height: 12, ...arsColorStyles[code] }}
    />
  );
}

/* ── Componente principal ── */
export function BarDiagramChangesPanel({ data, originalData, collapsed, onToggle, onReset }: Props) {
  const changes = useMemo(() => {
    const colorChanges: { group: string; phase: string; from: ArsColorCode; to: ArsColorCode }[] = [];
    const durationChanges: { phase: string; from: number; to: number }[] = [];
    const metadataChanges: { field: string; from: string; to: string }[] = [];

    // Colores
    for (let gi = 0; gi < data.groups.length; gi++) {
      const orig = originalData.groups[gi];
      const curr = data.groups[gi];
      if (!orig || !curr) continue;
      for (let ci = 0; ci < curr.cells.length; ci++) {
        if (curr.cells[ci] !== orig.cells[ci]) {
          colorChanges.push({
            group: curr.label,
            phase: data.phases[ci]?.label ?? `Col${ci}`,
            from: orig.cells[ci],
            to: curr.cells[ci],
          });
        }
      }
    }

    // Duraciones
    for (let i = 0; i < data.phases.length; i++) {
      const origDur = originalData.phases[i]?.duration;
      const currDur = data.phases[i].duration;
      if (origDur !== undefined && currDur !== origDur) {
        durationChanges.push({
          phase: data.phases[i].label,
          from: origDur,
          to: currDur,
        });
      }
    }

    // Metadata
    const metaLabels: Record<keyof DiagramMetadata, string> = {
      localitat: 'Localitat',
      cruilla: 'N.Cruilla',
      projecte: 'Projecte',
      data: 'Data',
      regulador: 'Regulador',
      planol: 'N. Planol',
      modificat: 'Modificat',
    };
    for (const key of Object.keys(metaLabels) as (keyof DiagramMetadata)[]) {
      if (data.metadata[key] !== originalData.metadata[key]) {
        metadataChanges.push({
          field: metaLabels[key],
          from: originalData.metadata[key] || '(buit)',
          to: data.metadata[key] || '(buit)',
        });
      }
    }

    return {
      colorChanges,
      durationChanges,
      metadataChanges,
      total: colorChanges.length + durationChanges.length + metadataChanges.length,
    };
  }, [data, originalData]);

  // No mostrar nada si no hay cambios
  if (changes.total === 0 && collapsed) return null;
  if (changes.total === 0) return null;

  /* Estado colapsado */
  if (collapsed) {
    return (
      <div className="w-10 flex-shrink-0 border-l border-gray-800 bg-gray-900/95 flex flex-col items-center pt-3">
        <button
          onClick={onToggle}
          className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300 transition-colors"
          title="Abrir panel de cambios"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="mt-3 flex flex-col items-center gap-1">
          <div className="relative">
            <GitCompareArrows className="w-4 h-4 text-orange-500" />
            <span className="absolute -top-1.5 -right-2 text-[9px] bg-orange-600 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
              {changes.total}
            </span>
          </div>
          <span
            className="text-[10px] text-gray-600 tracking-widest mt-1"
            style={{ writingMode: 'vertical-lr' }}
          >
            CANVIS
          </span>
        </div>
      </div>
    );
  }

  /* Estado expandido */
  return (
    <div className="w-72 flex-shrink-0 border-l border-gray-800 bg-gray-900/95 flex flex-col overflow-hidden">
      {/* Cabecera */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800 flex-shrink-0">
        <GitCompareArrows className="w-4 h-4 text-orange-400" />
        <span className="text-sm font-medium text-gray-200 flex-1">
          Canvis
          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-900/50 text-orange-400 font-medium">
            {changes.total}
          </span>
        </span>
        <button
          onClick={onReset}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-orange-400 hover:text-orange-300 hover:bg-orange-900/30 transition-colors"
          title="Restaurar tot a l'original"
        >
          <RotateCcw className="w-3 h-3" />
          Reset
        </button>
        <button
          onClick={onToggle}
          className="p-1 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300 transition-colors"
          title="Colapsar panel"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Secciones de cambios */}
      <div className="flex-1 overflow-y-auto">
        {/* Colores */}
        <Section icon={Palette} title="Colors" count={changes.colorChanges.length}>
          <div className="space-y-1.5">
            {changes.colorChanges.map((c, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500 w-8 flex-shrink-0">{c.group}</span>
                <span className="text-gray-600 w-6 flex-shrink-0">{c.phase}</span>
                <Swatch code={c.from} />
                <span className="text-gray-600">→</span>
                <Swatch code={c.to} />
                <span className="text-gray-500 text-[10px] truncate" title={arsColorLabels[c.to]}>
                  {arsColorLabels[c.to].split('—')[0].trim()}
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* Duraciones */}
        <Section icon={Clock} title="Duracions" count={changes.durationChanges.length}>
          <div className="space-y-1.5">
            {changes.durationChanges.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="text-gray-300 font-medium w-8">{d.phase}</span>
                <span className="text-red-400 line-through">{d.from}s</span>
                <span className="text-gray-600">→</span>
                <span className="text-green-400 font-medium">{d.to}s</span>
                <span className="text-gray-600 text-[10px]">
                  ({d.to - d.from > 0 ? '+' : ''}{d.to - d.from}s)
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* Metadata */}
        <Section icon={FileText} title="Metadades" count={changes.metadataChanges.length}>
          <div className="space-y-2">
            {changes.metadataChanges.map((m, i) => (
              <div key={i} className="text-xs">
                <div className="text-gray-500 font-medium mb-0.5">{m.field}</div>
                <div className="flex items-center gap-1.5">
                  <span className="text-red-400/70 line-through truncate max-w-[90px]" title={m.from}>{m.from}</span>
                  <span className="text-gray-600">→</span>
                  <span className="text-green-400 truncate max-w-[90px]" title={m.to}>{m.to}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
